import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  platformApi,
  savePlatformCredentials,
  clearPlatformCredentials,
  hasPlatformCredentials,
  type PlatformTenant,
  type PlatformAuditEntry,
  type ProvisionParams,
  type MigrationStatus,
} from '@/api/platformAdmin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// ─── Status badge colours ─────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active:            'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  suspended:         'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  provisioning:      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  schema_ready:      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  cognito_ready:     'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  payment_confirmed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  payment_failed:    'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  pending_payment:   'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  pending_deletion:  'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  deleted:           'bg-gray-200 text-gray-500 dark:bg-gray-700/50 dark:text-gray-400',
};

const PLAN_COLORS: Record<string, string> = {
  starter:      'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  professional: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  enterprise:   'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

// ─── Login form ───────────────────────────────────────────────────────────────

function LoginForm({ onLogin }: { onLogin: () => void }) {
  const [key, setKey] = useState('');
  const [actor, setActor] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!key) return;
    const apiUrl = (import.meta.env.VITE_API_URL as string ?? '').replace(/\/$/, '');
    savePlatformCredentials(apiUrl, key, actor || undefined);
    onLogin();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center text-xl">Platform Admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="key">Admin Key</Label>
              <Input id="key" type="password" autoComplete="current-password" placeholder="X-Platform-Admin-Key" value={key} onChange={(e) => setKey(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="actor">Your Email <span className="text-muted-foreground text-xs">(for audit log)</span></Label>
              <Input id="actor" type="email" autoComplete="email" placeholder="you@company.com" value={actor} onChange={(e) => setActor(e.target.value)} />
            </div>
            <Button type="submit" className="w-full">Sign in</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Provision dialog ─────────────────────────────────────────────────────────

function ProvisionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ProvisionParams>({
    tenantSlug: '', tenantName: '', adminEmail: '',
    adminFullName: '', plan: 'starter', initialUserCount: 1,
  });

  const mutation = useMutation({
    mutationFn: platformApi.provision,
    onSuccess: (data) => {
      toast({ title: 'Provisioned', description: data.message });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      onClose();
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.response?.data?.message ?? err.message, variant: 'destructive' });
    },
  });

  function set(k: keyof ProvisionParams, v: string | number) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Provision New Tenant</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Tenant Slug</Label>
              <Input placeholder="acme" value={form.tenantSlug} onChange={(e) => set('tenantSlug', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Tenant Name</Label>
              <Input placeholder="Acme Corp" value={form.tenantName} onChange={(e) => set('tenantName', e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Admin Email</Label>
            <Input type="email" placeholder="admin@acme.com" value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Admin Full Name</Label>
            <Input placeholder="Jane Smith" value={form.adminFullName} onChange={(e) => set('adminFullName', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Plan</Label>
              <Select value={form.plan} onValueChange={(v) => set('plan', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Initial Users</Label>
              <Input type="number" min={1} value={form.initialUserCount} onChange={(e) => set('initialUserCount', parseInt(e.target.value))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}>
            {mutation.isPending ? 'Provisioning…' : 'Provision'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Tenant detail drawer ─────────────────────────────────────────────────────

function ReprovisionDialog({ slug, tenantName, plan, onClose }: {
  slug: string; tenantName: string; plan: string; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFullName, setAdminFullName] = useState('');

  const mutation = useMutation({
    mutationFn: () => platformApi.provision({
      tenantSlug: slug,
      tenantName,
      adminEmail,
      adminFullName,
      plan: plan as ProvisionParams['plan'],
      initialUserCount: 1,
    }),
    onSuccess: (d) => {
      toast({ title: 'Re-provisioned', description: d.message });
      qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      qc.invalidateQueries({ queryKey: ['platform-tenant', slug] });
      onClose();
    },
    onError: (e: any) => toast({ title: 'Re-provision failed', description: e.response?.data?.message ?? e.message, variant: 'destructive' }),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Re-provision {slug}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Admin Email</Label>
            <Input type="email" placeholder="admin@company.com" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Admin Full Name</Label>
            <Input placeholder="Jane Smith" value={adminFullName} onChange={(e) => setAdminFullName(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !adminEmail || !adminFullName}>
            {mutation.isPending ? 'Running…' : 'Re-provision'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TenantDetail({ slug, onClose }: { slug: string; onClose: () => void }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['platform-tenant', slug],
    queryFn: () => platformApi.getTenant(slug),
  });

  const suspendMutation = useMutation({
    mutationFn: () => platformApi.suspend(slug),
    onSuccess: (d) => { toast({ title: 'Suspended', description: d.message }); qc.invalidateQueries({ queryKey: ['platform-tenants'] }); qc.invalidateQueries({ queryKey: ['platform-tenant', slug] }); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error ?? e.message, variant: 'destructive' }),
  });

  const unsuspendMutation = useMutation({
    mutationFn: () => platformApi.unsuspend(slug),
    onSuccess: (d) => { toast({ title: 'Unsuspended', description: d.message }); qc.invalidateQueries({ queryKey: ['platform-tenants'] }); qc.invalidateQueries({ queryKey: ['platform-tenant', slug] }); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error ?? e.message, variant: 'destructive' }),
  });

  const { data: migStatus, isLoading: migLoading, refetch: refetchMig } = useQuery<MigrationStatus>({
    queryKey: ['platform-tenant-migrations', slug],
    queryFn: () => platformApi.getMigrationStatus(slug),
  });

  const migrateMutation = useMutation({
    mutationFn: (dryRun: boolean) => platformApi.migrate(slug, dryRun),
    onSuccess: (d) => {
      toast({ title: d.appliedMigrations.length > 0 ? `${d.appliedMigrations.length} migration(s) applied` : 'Already up to date', description: d.message });
      refetchMig();
    },
    onError: (e: any) => toast({ title: 'Migration failed', description: e.response?.data?.error ?? e.message, variant: 'destructive' }),
  });

  const [showReprovision, setShowReprovision] = useState(false);

  const t = data?.tenant;
  const isSuspended = t?.status === 'suspended';

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{isLoading ? 'Loading…' : `${t?.name} (${slug})`}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="h-32 animate-pulse rounded bg-muted" />
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2">
              <Badge className={cn('capitalize', t?.status ? STATUS_COLORS[t.status] ?? '' : '')}>{t?.status}</Badge>
              <Badge className={cn('capitalize', t?.plan ? PLAN_COLORS[t.plan] ?? '' : '')}>{t?.plan}</Badge>
            </div>

            {data?.subscription && (
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p className="font-medium text-muted-foreground uppercase text-xs tracking-wide">Subscription</p>
                <p>Status: <span className="font-medium">{data.subscription.status}</span></p>
                <p>Seats: <span className="font-medium">{data.subscription.quantity}</span></p>
                {data.subscription.currentPeriodEnd && (
                  <p>Renews: <span className="font-medium">{new Date(data.subscription.currentPeriodEnd).toLocaleDateString()}</span></p>
                )}
              </div>
            )}

            {data?.features && data.features.length > 0 && (
              <div className="rounded-md border p-3 space-y-2">
                <p className="font-medium text-muted-foreground uppercase text-xs tracking-wide">Feature Flags</p>
                <div className="flex flex-wrap gap-2">
                  {data.features.map((f) => (
                    <Badge key={f.featureKey} variant={f.enabled ? 'default' : 'outline'} className="text-xs">
                      {f.featureKey}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-md border p-3 text-sm space-y-2">
              <p className="font-medium text-muted-foreground uppercase text-xs tracking-wide">DB Migrations</p>
              {migLoading ? (
                <div className="h-4 w-48 rounded bg-muted animate-pulse" />
              ) : migStatus ? (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">Current version:</span>
                    <span className="font-mono text-xs truncate max-w-xs">
                      {migStatus.currentVersion ?? <span className="italic text-muted-foreground">none applied</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground">Applied:</span>
                    <span className="font-medium">{migStatus.applied.length} / {migStatus.totalFiles}</span>
                    {migStatus.pending.length > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 font-medium">
                        {migStatus.pending.length} pending
                      </span>
                    )}
                    {migStatus.pending.length === 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 font-medium">
                        up to date
                      </span>
                    )}
                  </div>
                  {migStatus.pending.length > 0 && (
                    <div className="mt-1">
                      <p className="text-xs text-muted-foreground mb-1">Pending:</p>
                      <ul className="space-y-0.5">
                        {migStatus.pending.map((m) => (
                          <li key={m} className="font-mono text-xs text-muted-foreground pl-2 border-l-2 border-yellow-400">{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Could not load migration status.</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              {isSuspended ? (
                <Button size="sm" onClick={() => unsuspendMutation.mutate()} disabled={unsuspendMutation.isPending}>
                  {unsuspendMutation.isPending ? 'Unsuspending…' : 'Unsuspend'}
                </Button>
              ) : (
                <Button size="sm" variant="destructive" onClick={() => suspendMutation.mutate()} disabled={suspendMutation.isPending}>
                  {suspendMutation.isPending ? 'Suspending…' : 'Suspend'}
                </Button>
              )}
              {t?.status !== 'active' && (
                <Button size="sm" variant="outline" onClick={() => setShowReprovision(true)}>
                  Re-provision
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => migrateMutation.mutate(false)} disabled={migrateMutation.isPending}>
                {migrateMutation.isPending ? 'Running…' : 'Run Migrations'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => migrateMutation.mutate(true)} disabled={migrateMutation.isPending}>
                Dry Run
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    {showReprovision && t && (
      <ReprovisionDialog
        slug={slug}
        tenantName={t.name}
        plan={t.plan}
        onClose={() => setShowReprovision(false)}
      />
    )}
    </>
  );
}

// ─── Platform Audit Log view ──────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  'tenant.provisioned':       'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'tenant.suspended':         'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'tenant.unsuspended':       'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'tenant.deleted':           'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-300',
  'tenant.migration.applied': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  'tenant.migration.failed':  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

function PlatformAuditLog() {
  const [slugFilter, setSlugFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['platform-audit-log', slugFilter, offset],
    queryFn: () => platformApi.getAuditLog({
      ...(slugFilter ? { slug: slugFilter } : {}),
      limit: LIMIT,
      offset,
    }),
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Filter by tenant slug…"
          value={slugFilter}
          onChange={(e) => { setSlugFilter(e.target.value); setOffset(0); }}
          className="h-8 w-48 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {slugFilter && (
          <button onClick={() => { setSlugFilter(''); setOffset(0); }} className="text-xs text-muted-foreground hover:text-foreground">
            Clear
          </button>
        )}
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">When</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Actor</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Action</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Tenant</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Details</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b">
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 rounded bg-muted animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : (data?.items.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No audit entries yet.</td>
              </tr>
            ) : (
              data?.items.map((entry: PlatformAuditEntry) => (
                <tr key={entry.id} className="border-b">
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {new Date(entry.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm">{entry.actor}</td>
                  <td className="px-4 py-3">
                    <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', ACTION_COLORS[entry.action] ?? 'bg-gray-100 text-gray-700')}>
                      {entry.action.replace('tenant.', '')}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{entry.targetSlug ?? '—'}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs">
                    {entry.details ? (
                      <span className="font-mono">{JSON.stringify(entry.details)}</span>
                    ) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(data?.items.length ?? 0) > 0 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-muted-foreground">
            Showing {offset + 1}–{offset + (data?.items.length ?? 0)}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Previous</Button>
            <Button size="sm" variant="outline" disabled={(data?.items.length ?? 0) < LIMIT} onClick={() => setOffset(offset + LIMIT)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main dashboard ───────────────────────────────────────────────────────────

function PlatformDashboard({ onLogout }: { onLogout: () => void }) {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [showProvision, setShowProvision] = useState(false);
  const [tab, setTab] = useState<'tenants' | 'audit'>('tenants');

  const publicMigrateMutation = useMutation({
    mutationFn: (dryRun: boolean) => platformApi.migratePublic(dryRun),
    onSuccess: (d) => toast({
      title: d.appliedMigrations && d.appliedMigrations.length > 0
        ? `${d.appliedMigrations.length} public migration(s) applied`
        : d.wouldApply
        ? `Dry run: ${d.wouldApply.length} would apply`
        : 'Already up to date',
      description: d.message,
    }),
    onError: (e: any) => toast({ title: 'Public migration failed', description: e.response?.data?.error ?? e.message, variant: 'destructive' }),
  });

  const { data: tenants, isLoading, error, refetch } = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: platformApi.listTenants,
    retry: 1,
  });

  function handleLogout() {
    clearPlatformCredentials();
    onLogout();
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="w-full max-w-sm">
          <CardContent className="pt-6 space-y-4">
            <p className="font-medium text-center">Connection failed</p>
            <p className="text-destructive text-center text-sm">
              {(error as any)?.response?.status === 403
                ? 'Invalid admin key.'
                : 'Could not reach the API. Check the URL includes the stage path (e.g. .../dev).'}
            </p>
            <Button className="w-full" onClick={() => refetch()}>Retry</Button>
            <Button className="w-full" variant="outline" onClick={handleLogout}>Sign out &amp; re-enter credentials</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusOrder: Record<string, number> = { active: 0, provisioning: 1, suspended: 2, deleted: 3 };
  const tenantList = Array.isArray(tenants) ? tenants : [];
  const sorted = [...tenantList].sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Platform Admin</h1>
          <p className="text-xs text-muted-foreground mt-0.5">RepairApp tenant management</p>
        </div>
        <div className="flex gap-2">
          {tab === 'tenants' && (
            <Button size="sm" onClick={() => setShowProvision(true)}>+ Provision Tenant</Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={publicMigrateMutation.isPending}
            onClick={() => publicMigrateMutation.mutate(false)}
            title="Apply any unapplied public schema migrations"
          >
            {publicMigrateMutation.isPending ? 'Migrating…' : 'Run Public Migrations'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => refetch()}>Refresh</Button>
          <Button size="sm" variant="outline" onClick={handleLogout}>Sign out</Button>
        </div>
      </header>

      {/* Stats bar */}
      {!isLoading && tenantList.length > 0 && (
        <div className="border-b bg-muted/30 px-6 py-3 flex gap-6 text-sm">
          <span><strong>{tenantList.length}</strong> tenants</span>
          <span><strong>{tenantList.filter((t) => t.status === 'active').length}</strong> active</span>
          <span><strong>{tenantList.filter((t) => t.status === 'suspended').length}</strong> suspended</span>
          <span><strong>{tenantList.filter((t) => t.plan === 'enterprise').length}</strong> enterprise</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b bg-card px-6 flex gap-0">
        {(['tenants', 'audit'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors capitalize',
              tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t === 'tenants' ? 'Tenants' : 'Audit Log'}
          </button>
        ))}
      </div>

      <div className="p-6">
        {tab === 'tenants' && (
          <div className="rounded-md border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Tenant</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Slug</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Plan</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Created</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b">
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 rounded bg-muted animate-pulse" /></td>
                      ))}
                    </tr>
                  ))
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No tenants yet. Provision one to get started.
                    </td>
                  </tr>
                ) : (
                  sorted.map((t: PlatformTenant) => (
                    <tr key={t.id} className="border-b cursor-pointer hover:bg-muted/40 transition-colors" onClick={() => setSelectedSlug(t.slug)}>
                      <td className="px-4 py-3 font-medium">{t.name}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{t.slug}</td>
                      <td className="px-4 py-3">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium capitalize', PLAN_COLORS[t.plan] ?? '')}>{t.plan}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium capitalize', STATUS_COLORS[t.status] ?? '')}>
                          {t.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{new Date(t.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'audit' && <PlatformAuditLog />}
      </div>

      {showProvision && <ProvisionDialog open onClose={() => setShowProvision(false)} />}
      {selectedSlug && <TenantDetail slug={selectedSlug} onClose={() => setSelectedSlug(null)} />}
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function PlatformAdmin() {
  const [authed, setAuthed] = useState(hasPlatformCredentials);
  return authed
    ? <PlatformDashboard onLogout={() => setAuthed(false)} />
    : <LoginForm onLogin={() => setAuthed(true)} />;
}
